<template>
  <UPage class="h-[calc(100vh-4rem)] flex flex-col overflow-hidden bg-default">
    <UPageHeader :title="headerTitle">
      <template #description>
        <div class="flex items-center gap-2">
          <UBadge v-if="blueprint.id" color="neutral" variant="subtle" class="font-mono text-xs">{{
            blueprint.id
          }}</UBadge>
          <UBadge v-if="blueprint.version" color="neutral" size="xs" variant="subtle">v{{ blueprint.version }}</UBadge>
          <div v-if="blueprint.id" class="text-xs text-muted flex items-center gap-1 ml-2">
            <UIcon
              name="i-heroicons-check-circle"
              class="w-4 h-4"
              :class="lastSaved ? 'text-success' : 'text-muted'"
            />
            Zuletzt gespeichert: {{ lastSaved || '---' }}
          </div>
        </div>
      </template>

      <template #links>
        <UButton
          v-if="viewOnly"
          color="neutral"
          icon="i-heroicons-arrow-left"
          variant="ghost"
          @click="goBack"
        />
        <UButton
          v-else
          color="neutral"
          variant="subtle"
          icon="i-heroicons-arrow-left"
          label="Verwerfen"
          @click="showDiscardModal = true"
        />
        <UButton
          v-if="viewOnly"
          color="primary"
          icon="i-heroicons-pencil-square"
          label="Bearbeiten"
          @click="router.push(`/admin/workflow/${blueprintId}/edit`)"
        />
        <UButton
          v-else
          :loading="isSaving"
          color="primary"
          icon="i-heroicons-cloud-arrow-up"
          label="Speichern"
          @click="showSaveModal = true"
        />
      </template>
    </UPageHeader>

    <UPageBody class="flex-1 flex p-0 sm:p-0 overflow-hidden min-h-0 h-[calc(100vh-20rem)]">
      <!-- YAML Editor Area (Left) -->
      <div
        v-if="!viewOnly"
        class="pr-2 w-1/2 flex flex-col border-r-3 border-default bg-default min-h-0 h-full"
      >
        <div
          class="pl-1 py-2 flex flex-col gap-2 bg-default flex-shrink-0"
        >
          <div class="flex items-center gap-3 flex-shrink-0">
            <span class="text-xs font-bold uppercase text-muted">YAML-Konfiguration</span>
            <UBadge v-if="yamlError" color="error" size="xs" variant="subtle">Fehlerhaft</UBadge>
            <UBadge v-else-if="yamlWarning" color="warning" size="xs" variant="subtle">Warnung</UBadge>
            <UBadge v-else color="success" size="xs" variant="subtle">Gültig</UBadge>
          </div>
          <div
            v-if="yamlError"
            class="text-[10px] text-error font-mono font-medium whitespace-pre-wrap overflow-y-auto max-h-32 flex gap-1.5 items-start"
            :title="yamlError"
          >
            <UIcon name="i-heroicons-exclamation-circle" class="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>{{ yamlError }}</span>
          </div>
          <div
            v-else-if="yamlWarning"
            class="text-[10px] text-amber-500 font-mono font-medium whitespace-pre-wrap overflow-y-auto max-h-32 flex gap-1.5 items-start"
            :title="yamlWarning"
          >
            <UIcon name="i-heroicons-exclamation-triangle" class="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>{{ yamlWarning }}</span>
          </div>
        </div>
        <div class="flex-1 min-h-0 overflow-scroll rounded-md">
          <Codemirror
            v-model="yamlCode"
            :autofocus="true"
            :extensions="extensions"
            :indent-with-tab="true"
            :style="{ height: '100%', fontSize: '14px' }"
            :tab-size="2"
            class="h-full w-full"
            placeholder="YAML-Konfiguration..."
          />
        </div>
      </div>

      <!-- Designer Area (Right) -->
      <div
        class="flex flex-col bg-default relative min-h-0 overflow-hidden"
        :class="viewOnly ? 'w-full px-4' : 'pl-2 w-1/2'"
      >
        <div class="pl-1 py-2 flex items-center justify-between bg-default flex-shrink-0">
          <span class="text-xs font-bold uppercase text-muted">Designer Vorschau</span>
          <div class="text-[10px] text-muted italic">Visualisierung der YAML-Struktur</div>
        </div>
        <div class="flex-1 min-h-0 relative overflow-hidden">
          <WorkflowCanvas v-if="blueprint.id" :blueprint="blueprint" :read-only="viewOnly" />
          <div v-else class="h-full flex items-center justify-center text-muted text-sm italic">
            Warten auf Blueprint-Daten...
          </div>
        </div>
      </div>
    </UPageBody>

    <UModal
      v-model:open="showDiscardModal"
      title="Änderungen verwerfen?"
      description="Möchten Sie die ungespeicherten Änderungen wirklich verwerfen? Alle Änderungen gehen verloren."
    >
      <template #title>
        <div class="flex items-center gap-2">
          <UIcon name="i-heroicons-exclamation-triangle" class="w-5 h-5 text-error" />
          <span>Änderungen verwerfen?</span>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-end gap-2">
          <UButton
            color="neutral"
            variant="ghost"
            label="Abbrechen"
            @click="showDiscardModal = false"
          />
          <UButton color="error" label="Verwerfen" @click="goBack" />
        </div>
      </template>
    </UModal>

    <UModal
      v-model:open="showSaveModal"
      title="Workflow speichern?"
      description="Möchten Sie den Workflow wirklich speichern? Die Änderungen sind sofort aktiv."
    >
      <template #title>
        <div class="flex items-center gap-2">
          <UIcon name="i-heroicons-cloud-arrow-up" class="w-5 h-5 text-primary" />
          <span>Workflow speichern?</span>
        </div>
      </template>

      <template #description>
        <div class="space-y-2">
          <p>Möchten Sie den Workflow wirklich speichern? Die Änderungen sind sofort aktiv.</p>
          <div
            v-if="yamlWarning"
            class="p-2 rounded bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-xs flex items-start gap-2"
          >
            <UIcon name="i-heroicons-exclamation-triangle" class="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{{ yamlWarning }}</span>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-end gap-2">
          <UButton
            color="neutral"
            variant="ghost"
            label="Abbrechen"
            @click="showSaveModal = false"
          />
          <UButton color="primary" label="Speichern" @click="confirmSave" />
        </div>
      </template>
    </UModal>
  </UPage>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import * as yaml from 'js-yaml'
import { Codemirror } from 'vue-codemirror'
import { yaml as yamlLang } from '@codemirror/lang-yaml'
import { oneDark } from '@codemirror/theme-one-dark'
import { basicSetup } from 'codemirror'
import type { WorkflowBlueprint, WorkflowStepConfig } from '@/types'
import WorkflowCanvas from '@/components/workflow/WorkflowCanvas.vue'

const props = defineProps({
  customerNo: {
    type: String,
    required: false,
    default: '',
  },
  tenantName: {
    type: String,
    required: false,
    default: '',
  },
  blueprintId: {
    type: String,
    required: false,
    default: '',
  },
  viewOnly: {
    type: Boolean,
    default: false,
  },
})

const router = useRouter()
const toast = useToast()
const extensions = [basicSetup, yamlLang(), oneDark]

const blueprint = ref<WorkflowBlueprint>({
  id: '',
  steps: [],
})

const yamlCode = ref('')
const yamlError = ref<string | null>(null)
const yamlWarning = ref<string | null>(null)
const lastSaved = ref<string | null>(null)
const isSaving = ref(false)
const loading = ref(false)

const headerTitle = computed(() => {
  if (router.currentRoute.value.query.instanceId) return 'Workflow Instanz Blueprint'
  return props.viewOnly ? 'Workflow Ansicht' : 'Workflow Editor'
})

const showDiscardModal = ref(false)
const showSaveModal = ref(false)

const goBack = () => {
  if (router.currentRoute.value.query.instanceId) {
    router.push('/tasks')
  } else {
    router.push('/admin/workflow')
  }
}

const confirmSave = () => {
  showSaveModal.value = false
  saveBlueprint()
}

const loadBlueprint = async () => {
  const instanceId = router.currentRoute.value.query.instanceId

  if (!props.blueprintId || props.blueprintId === 'new') {
    // New Blueprint Template
    blueprint.value = {
      id: 'new-workflow',
      version: 1,
      trigger: {
        type: 'status',
        topic: '20',
        objectType: 'Receipt',
      },
      steps: [],
    }
    updateYamlCode()
    return
  }

  loading.value = true
  try {
    let url = `/api/workflow/v1/blueprints/${props.blueprintId}`
    if (instanceId) {
      url = `/api/workflow/v1/tasks/${instanceId}/blueprint`
    }

    const response = await axios.get(url)
    blueprint.value = response.data
    if (blueprint.value.updatedAt) {
      lastSaved.value = new Date(blueprint.value.updatedAt).toLocaleTimeString()
    }
    updateYamlCode()
  } catch (error) {
    console.error('Failed to load blueprint', error)
    let errorMessage = 'Der Workflow Blueprint konnte nicht geladen werden.'
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      errorMessage = error.response.data.error
    }
    toast.add({
      title: 'Fehler beim Laden',
      description: errorMessage,
      color: 'red',
    })
    goBack()
  } finally {
    loading.value = false
  }
}

const updateYamlCode = () => {
  try {
    const cleanBlueprint = JSON.parse(JSON.stringify(blueprint.value))
    if (cleanBlueprint.steps) {
      cleanBlueprint.steps.forEach((step: WorkflowStepConfig) => {
        delete step.position
        delete step.style
      })
    }
    yamlCode.value = yaml.dump(cleanBlueprint, {
      indent: 2,
      lineWidth: -1,
      noRefs: true,
      skipInvalid: true,
    })
    yamlError.value = null
  } catch (e) {
    console.error('Failed to generate YAML', e)
    yamlCode.value = '# Fehler bei der Generierung von YAML'
  }
}

// Watch for YAML changes and update blueprint
watch(yamlCode, (newVal) => {
  try {
    const parsed = yaml.load(newVal) as WorkflowBlueprint
    if (!parsed || typeof parsed !== 'object') return

    const validationError = validateBlueprint(parsed)
    if (validationError) {
      yamlError.value = validationError
      yamlWarning.value = null
      return
    }

    blueprint.value = JSON.parse(JSON.stringify(parsed))
    yamlError.value = null
    yamlWarning.value = getBlueprintWarnings(parsed)
  } catch (e) {
    if (e instanceof Error) {
      yamlError.value = e.message || 'Parsing Fehler'
    } else {
      yamlError.value = 'Parsing Fehler'
    }
    yamlWarning.value = null
  }
})

const getBlueprintWarnings = (bp: WorkflowBlueprint): string | null => {
  if (!bp || !bp.steps || !Array.isArray(bp.steps)) return null

  for (const step of bp.steps) {
    if (step.stepType === 'UserStep' && (!step.group || step.group.trim() === '')) {
      return `Schritt "${step.name}" (UserStep): Gruppe ist ein Pflichtfeld (sollte nicht leer sein).`
    }
  }

  return null
}

const validateBlueprint = (bp: WorkflowBlueprint): string | null => {
  if (!bp.id) return 'Blueprint ID fehlt'
  if (!bp.steps || !Array.isArray(bp.steps)) return 'Steps Liste fehlt oder ist ungültig'

  if (bp.overviewField1) {
    if (!bp.overviewField1.name) return 'Overview Field 1 hat keinen Namen'
    if (!bp.overviewField1.label) return 'Overview Field 1 hat kein Label'
  }
  if (bp.overviewField2) {
    if (!bp.overviewField2.name) return 'Overview Field 2 hat keinen Namen'
    if (!bp.overviewField2.label) return 'Overview Field 2 hat kein Label'
  }

  const stepNames = new Set(bp.steps.map((s) => s.name))

  for (const step of bp.steps) {
    if (!step.name) return 'Schritt ohne Namen gefunden'
    if (!step.stepType) return `Schritt "${step.name}" hat keinen stepType`

    if (step.nextSteps) {
      for (const next of step.nextSteps) {
        if (next.step && next.step !== 'COMPLETED' && next.step !== 'APPROVED' && next.step !== 'REJECTED' && !stepNames.has(next.step)) {
          return `Schritt "${step.name}" verweist auf unbekannten nächsten Schritt "${next.step}"`
        }
      }
    }
  }

  if (bp.trigger?.nextStep?.step) {
    const nextStep = bp.trigger.nextStep.step
    if (!stepNames.has(nextStep)) {
      return `Trigger verweist auf unbekannten nächsten Schritt "${nextStep}"`
    }
  }

  return null
}

const saveBlueprint = async () => {
  if (yamlError.value) {
    toast.add({
      title: 'Validierungsfehler',
      description: 'Bitte korrigieren Sie die Fehler im YAML-Code bevor Sie speichern.',
      color: 'red',
    })
    return
  }

  if (!blueprint.value.id) {
    toast.add({
      title: 'Validierungsfehler',
      description: 'Bitte geben Sie eine ID für den Workflow an.',
      color: 'red',
    })
    return
  }

  isSaving.value = true
  try {
    await axios.put(`/api/workflow/v1/blueprints/${blueprint.value.id}`, blueprint.value)
    lastSaved.value = new Date().toLocaleTimeString()
    yamlError.value = null
    toast.add({
      title: 'Erfolgreich gespeichert',
      description: `Der Workflow "${blueprint.value.id}" wurde erfolgreich gespeichert.`,
      color: 'green',
    })

    // If we just created a new one, update the URL to point to the new ID
    if (props.blueprintId === 'new' || props.blueprintId === '') {
      router.replace(`/admin/workflow/${blueprint.value.id}/edit`)
    }
  } catch (error) {
    console.error('Failed to save blueprint', error)
    let errorMessage = 'Der Workflow konnte nicht gespeichert werden.'
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      errorMessage = error.response.data.error
    }

    yamlError.value = errorMessage

    toast.add({
      title: 'Fehler beim Speichern',
      description: errorMessage,
      color: 'red',
    })
  } finally {
    isSaving.value = false
  }
}

onMounted(() => {
  loadBlueprint()
})
</script>
